import React from "react"
import { AnimatePresence } from "framer-motion"

// All sections - lazy loading temporarily disabled
import { AudienceSection } from "@/components/public/AudienceSection"
import { CtaSection } from "@/components/public/CtaSection"
import { FAQAccordion, type FAQItem } from "@/components/public/FAQAccordion"
import { FeaturesSection } from "@/components/public/FeaturesSection"
import { GeoCoverage } from "@/components/public/GeoCoverage"
import { HeroSection } from "@/components/public/HeroSection"
import { LandingFooter } from "@/components/public/LandingFooter"
import { ProductsSection } from "@/components/public/ProductsSection"
import { FlowAnimationSection } from "@/components/public/FlowAnimationSection"
import { PublicHeader } from "@/components/public/PublicHeader"
// Detailed sections moved to internal pages:
// - RoutesDiagram + PaymentModels → /how-payments-work
// - Testimonials → moved (weak social proof for early stage)
// Note: GeoCoverage kept on main landing (interactive, functional) + available on /coverage
import { SoloFounderSection } from "@/components/public/SoloFounderSection"
import { HowItWorksSection } from "@/components/public/HowItWorksSection"
import { TrustComplianceSection } from "@/components/public/TrustComplianceSection"

// UI Components
import { Reveal } from "@/components/ui/Reveal"

// Branded Preloader (2025-2026 style - no boring skeletons!)
import { LandingPreloader, ContentReveal } from "@/components/public/LandingPreloader"

import { ErrorBoundary } from "@/components/ui/ErrorBoundary"
import { useHashRoute } from "@/hooks/useHashRoute"
import { useProgressiveEnhancement } from "@/hooks/useProgressiveEnhancement"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { Page } from "@supabank/ui"
import { useTranslation } from "react-i18next"

export default function LandingPage() {
  const { t } = useTranslation("marketing")

  // FAQ items from SSOT (marketing.json)
  const faqItems: FAQItem[] = ((t("faq.items", { returnObjects: true }) as any[]) || []).map((item: any) => ({
    id: item.id,
    q: item.question,
    a: item.answer,
  }))
  const prefersReducedMotion = useReducedMotion();
  const { connectionSpeed } = useProgressiveEnhancement();
  const isSlowConnection = connectionSpeed === 'slow';
  const [showPreloader, setShowPreloader] = React.useState(true);
  const [contentReady, setContentReady] = React.useState(false);
  const { hash } = useHashRoute(); // For aria-current (BUG-020)

  // Branded preloader for initial load (replaces boring skeletons)
  // Duration adapts to connection speed - slower for better readability
  const preloaderDuration = isSlowConnection ? 5500 : 4000;

  const handlePreloaderComplete = React.useCallback(() => {
    setShowPreloader(false);
    // Small delay for smooth content reveal
    setTimeout(() => setContentReady(true), 100);
  }, []);

  // Fast-track for reduced motion preference
  React.useEffect(() => {
    if (prefersReducedMotion) {
      setShowPreloader(false);
      setContentReady(true);
    }
  }, [prefersReducedMotion]);

  // Determine current section from hash (BUG-020)
  const currentHash = hash ? hash.slice(1) : ''; // Remove #

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      {/* Branded Preloader - 2025-2026 style, replaces boring skeletons */}
      <AnimatePresence>
        {showPreloader && (
          <LandingPreloader
            duration={preloaderDuration}
            onComplete={handlePreloaderComplete}
          />
        )}
      </AnimatePresence>

      {/* Unified PublicHeader component */}
      <PublicHeader currentSection={currentHash} />

      <main className="space-y-0" id="main" role="main">
        {/* Skip to main content link for keyboard users (BUG-023) */}
        <a
          href="#main"
          className={cn(
            "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4",
            "focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white",
            "focus:rounded-md focus:shadow-lg",
            semanticTokens.focus.ring
          )}
        >
          Skip to main content
        </a>

        {/* Core sections on landing - with blur→sharp content reveal */}
        <ContentReveal isReady={contentReady}>
          <ErrorBoundary level="section">
            <Reveal disabled={prefersReducedMotion}><HeroSection id="hero" /></Reveal>
          </ErrorBoundary>
          {/* How It Works - P1 Landing Blocks: Process explanation with icons */}
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><HowItWorksSection id="how-it-works" /></Reveal>
          </ErrorBoundary>
          {/* Product Showcase - Show product before words */}
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><FlowAnimationSection id="flow-animation" /></Reveal>
          </ErrorBoundary>
          {/* Below-fold sections with lazy loading (BUG-025) */}
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><AudienceSection id="audience" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><FeaturesSection id="features" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><ProductsSection id="products" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><GeoCoverage id="coverage" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><SoloFounderSection id="founder" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><TrustComplianceSection id="trust" /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><FAQAccordion id="faq" items={faqItems} /></Reveal>
          </ErrorBoundary>
          <ErrorBoundary level="section">
            <Reveal delay={0.1} disabled={prefersReducedMotion} lazy={true}><CtaSection id="cta" /></Reveal>
          </ErrorBoundary>

          {/* aria-live region for dynamic content announcements (BUG-024) */}
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            id="landing-announcements"
          />
        </ContentReveal>

        {/* Detailed sections moved to internal pages:
            - PaymentModels + RoutesDiagram → /how-payments-work
            - TestimonialsSection → removed (weak social proof for early stage)
            Note: GeoCoverage kept on main landing (interactive, functional) + available on /coverage
            These pages preserve all content while reducing cognitive load on main landing */}
      </main>

      <LandingFooter />
    </Page>
  )
}